import { AlertCircle, Loader2 } from 'lucide-react';
import type { TaskWithRelations } from '@vagusx/clients';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { useClassificationLabelMap } from '@/hooks/use-classification-label';
import { useTask } from '@/hooks/use-tasks';
import { TaskWorkPanel } from '@/pages/tasks/TaskWorkPanel';

/**
 * One referral's task work panel in a slide-over: the same panel as the task
 * inbox, on its own. Opened from the Referrals worklist and from a campaign's
 * queue; each keeps which referral is open in its own URL.
 *
 * The host's route must declare `call` in its search schema: the panel opens a
 * call's detail through `useCallDetailParam`, which writes `?call=`.
 */
export function ReferralSheet({
  taskId,
  loaded,
  onClose,
}: {
  /** The referral's task id; the sheet is open while there is one. */
  taskId: string | undefined;
  /** The task when the host already has it on screen, so nothing is fetched. */
  loaded?: TaskWithRelations | null;
  onClose: () => void;
}): React.ReactElement {
  const labelMap = useClassificationLabelMap();
  // Prefer the row already on screen; fetch by id only for a COLD deep link —
  // a pasted URL, a reload, or a referral the host does not have loaded.
  // Without the fallback the sheet would silently stay shut for exactly the
  // case the URL exists to serve. `TaskResponse` is structurally a
  // `TaskWithRelations` (the two join-derived fields are optional), so the
  // fetched task drives the same panel, just without the pre-joined patient
  // name until the panel loads its own.
  const { data: fetched, isError: fetchError } = useTask(taskId && !loaded ? taskId : undefined);
  const task: TaskWithRelations | null = loaded ?? fetched ?? null;

  return (
    <Sheet open={!!taskId} onOpenChange={(next) => !next && onClose()}>
      {/* The task panel draws its own close button in its toolbar, so the
          sheet's corner X (which overlaps Done) is only needed while the
          panel isn't mounted — the loading and error states. */}
      <SheetContent
        className="flex w-[90vw] flex-col gap-0 p-0 sm:max-w-[90vw]"
        showCloseButton={!task}
        onOpenAutoFocus={(e) => {
          // While loading, the corner X is the only focusable element and
          // it unmounts when the task lands, which would drop focus. Focus
          // the sheet itself instead; it stays mounted across the swap.
          if (task) return;
          e.preventDefault();
          if (e.currentTarget instanceof HTMLElement) e.currentTarget.focus();
        }}
      >
        <SheetHeader className="sr-only">
          <SheetTitle>Task details</SheetTitle>
        </SheetHeader>
        {task ? (
          <div className="min-h-0 flex-1">
            <TaskWorkPanel key={task.id} task={task} labelMap={labelMap} onClose={onClose} />
          </div>
        ) : fetchError ? (
          // The fetch has SETTLED and failed — a deleted referral, or one
          // in another org. It will not resolve on its own, so an
          // indefinite spinner would read as "still working" for ever.
          <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-3 text-sm text-muted-foreground">
            <AlertCircle className="h-5 w-5 text-destructive" aria-hidden="true" />
            <span>Couldn&apos;t load this referral. It may have been deleted.</span>
            <Button variant="outline" size="sm" onClick={onClose}>
              Close
            </Button>
          </div>
        ) : (
          // Open-but-empty is reachable now that the URL drives the sheet: a
          // cold deep link opens it before the by-id fetch lands. Say we're
          // loading rather than rendering an empty slide-over.
          <div className="flex min-h-0 flex-1 items-center justify-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading referral…
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
